/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Registration event creation/editing (REGISTRATION_EVENT).
 * Banner upload follows the same presigned-URL flow as Business, Virtual, Breakfast, and B2B events.
 * @module logic/eventCreation
 */

import {
  fetchUserRoleData,
  getAuthJsonHeaders,
  normalizeAudienciaFromApi,
  resolveExpiredToken,
  uploadEventBannerFromDataUrl,
} from "../../utils/eventApi.js";
import dayjs from "dayjs";

const DATE_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";
const REGISTRATION_EVENTS_MANAGE_PATH = "/manageEvents";

export const getUserRoleData = fetchUserRoleData;

/**
 * Formats a date-like value to `YYYY-MM-DD` in UTC for HTML date inputs.
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
const formatDateField = (value) =>
  value ? dayjs(value).utc().format(DATE_FORMAT_YYYY_MM_DD) : null;

/**
 * @param {Record<string, unknown>} eventData
 * @returns {Record<string, unknown>}
 */
const buildRegistrationEventSubmitBody = (eventData) => ({
  userId: eventData.userId,
  nombre: eventData.nombre,
  descripcion: eventData.descripcion ?? null,
  fechaEvento: eventData.fechaEvento,
  categoria: eventData.categoria ?? null,
  audiencia: normalizeAudienciaFromApi(eventData.audiencia),
  hora: eventData.hora ?? null,
  duracion: eventData.duracion ?? null,
  costoSocios: eventData.costoSocios ?? null,
  costoNoSocios: eventData.costoNoSocios ?? null,
  cupoMaximo: eventData.cupoMaximo ?? null,
  ubicacion: eventData.ubicacion ?? null,
  fechaDisponibleSocios: eventData.fechaDisponibleSocios ?? null,
  fechaDisponibleNoSocios: eventData.fechaDisponibleNoSocios ?? null,
  fechaLimiteRegistro: eventData.fechaLimiteRegistro ?? null,
  interesesComercialesRequerido:
    eventData.interesesComercialesRequerido ?? false,
  expectativasRequerido: eventData.expectativasRequerido ?? false,
  activo: eventData.activo ?? true,
  correoRequerido: eventData.correoRequerido ?? false,
  fechaDeCreacion: eventData.fechaDeCreacion,
  imagenBanner: eventData.imagenBanner || null,
  nombreImagen: eventData.nombreImagen || undefined,
  formatoImagen: eventData.formatoImagen || undefined,
});

/**
 * Fetches event details and formats dates for form display.
 * @async
 * @param {string} eventId
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Function} setFormData
 * @returns {Promise<void>}
 */
export const getEventDetails = async (eventId, navigate, setFormData) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}event/${eventId}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getEventDetails(eventId, navigate, setFormData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (!response.ok) {
      return;
    }

    setFormData({
      ...data,
      fechaEvento: formatDateField(data.fechaEvento),
      fechaDisponibleSocios: formatDateField(data.fechaDisponibleSocios),
      fechaDisponibleNoSocios: formatDateField(data.fechaDisponibleNoSocios),
      fechaLimiteRegistro: formatDateField(data.fechaLimiteRegistro),
      audiencia: normalizeAudienciaFromApi(data.audiencia),
    });
  } catch (error) {
    console.error(error);
  }
};

/**
 * Creates or updates a registration event with optional banner upload via presigned URL.
 * @async
 * @param {Record<string, unknown>} eventData
 * @param {string|null|undefined} eventId
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Function} setSaving
 * @param {Function} setHasChanges
 * @returns {Promise<{ ok: boolean, wasCreate?: boolean, event?: Record<string, unknown> }|undefined>}
 */
export const submitEvent = async (
  eventData,
  eventId,
  navigate,
  setSaving,
  setHasChanges,
) => {
  setSaving(true);
  const wasCreate = !eventId;
  const { Buffer } = await import("buffer");

  try {
    const body = buildRegistrationEventSubmitBody(eventData);
    const url = eventId
      ? `${process.env.REACT_APP_ROOT_API}event/${eventId}`
      : `${process.env.REACT_APP_ROOT_API}event`;
    const method = eventId ? "PUT" : "POST";

    console.log("Submitting event", {
      method,
      url,
      eventId: eventId ?? null,
      wasCreate,
      eventData,
      body,
    });

    const response = await fetch(url, {
      method,
      headers: getAuthJsonHeaders(),
      body: JSON.stringify(body),
    });

    const data = await response.json();

    console.log("API response", {
      ok: response.ok,
      status: response.status,
      data,
    });

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      submitEvent(eventData, eventId, navigate, setSaving, setHasChanges),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        console.log("Token refresh redirected to sign-in");
        return { ok: false };
      }
      return tokenResult.result;
    }

    if (response.ok && data.event) {
      if (data.presignedUrl) {
        console.log("Uploading banner to S3", {
          presignedUrl: data.presignedUrl,
          eventData,
        });
        try {
          await uploadEventBannerFromDataUrl(
            data.presignedUrl,
            eventData.imagenBanner,
            eventData.formatoImagen,
            Buffer,
          );
          console.log("Banner uploaded to S3 successfully");
        } catch (uploadError) {
          console.error("Banner S3 upload failed", uploadError);
          throw uploadError;
        }
      } else {
        console.log(
          "No presignedUrl in response; skipping S3 upload",
          eventData,
        );
      }

      setHasChanges(false);
      console.log("Navigating to manage events", {
        path: REGISTRATION_EVENTS_MANAGE_PATH,
      });
      navigate(REGISTRATION_EVENTS_MANAGE_PATH, {
        state: { eventCreated: true },
      });
      return { ok: true, wasCreate, event: data.event };
    }

    console.error("Submit failed", {
      status: response.status,
      data,
    });
    return { ok: false };
  } catch (error) {
    console.error("Submit error", error);
    return { ok: false };
  } finally {
    setSaving(false);
  }
};

/**
 * Initializes the registration event create/edit page.
 * @async
 * @param {string|null|undefined} eventId
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Function} setFormData
 * @param {Function} setLoading
 * @returns {Promise<void>}
 */
export const initializeEventCreation = async (
  eventId,
  userId,
  changePermissions,
  navigate,
  setFormData,
  setLoading,
) => {
  try {
    await getUserRoleData(userId, changePermissions, navigate);
    if (eventId) {
      await getEventDetails(eventId, navigate, setFormData);
    }
  } catch (error) {
    console.error("Error initializing event creation:", error);
  } finally {
    setLoading(false);
  }
};
